// Validate + compress an image File down to <= maxBytes by resizing + re-encoding.
// Throws if the file isn't a real image, is too large to accept, or still
// exceeds the limit after compression (e.g. SVG/GIF).

export const MAX_IMAGE_BYTES = 100 * 1024; // 100 KB target output
export const MAX_UPLOAD_BYTES = 25 * 1024 * 1024; // 25 MB largest accepted input

export const ACCEPTED_IMAGE_TYPES = [
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/avif",
  "image/gif",
  "image/svg+xml",
] as const;

/** `accept` attribute value for every image <input type="file"> in the app. */
export const IMAGE_ACCEPT_ATTR = ACCEPTED_IMAGE_TYPES.join(",");

const UNCOMPRESSIBLE = ["image/svg+xml", "image/gif"];

function ascii(bytes: Uint8Array, start: number, len: number) {
  let s = "";
  for (let i = start; i < start + len && i < bytes.length; i++) s += String.fromCharCode(bytes[i]!);
  return s;
}

/** Detect the real image type from magic bytes, ignoring the (spoofable) File.type. */
function sniffImageType(bytes: Uint8Array): string | null {
  if (bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff) return "image/jpeg";
  if (bytes[0] === 0x89 && ascii(bytes, 1, 3) === "PNG") return "image/png";
  if (ascii(bytes, 0, 3) === "GIF") return "image/gif";
  if (ascii(bytes, 0, 4) === "RIFF" && ascii(bytes, 8, 4) === "WEBP") return "image/webp";
  if (ascii(bytes, 4, 4) === "ftyp") {
    const brand = ascii(bytes, 8, 4);
    if (brand.startsWith("avi")) return "image/avif";
    if (brand.startsWith("hei") || brand.startsWith("mif")) return "image/heic";
    return null;
  }
  const head = ascii(bytes, 0, 256).trim().toLowerCase();
  if (head.startsWith("<svg") || (head.startsWith("<?xml") && head.includes("<svg"))) return "image/svg+xml";
  return null;
}

export type ValidatedImage = { file: File; type: string };

/**
 * Strict client-side gate for every image upload: rejects empty files,
 * oversized files, and anything whose bytes are not a supported image
 * (a renamed .exe/.pdf never reaches storage).
 */
export async function validateImageFile(
  file: File,
  { maxInputBytes = MAX_UPLOAD_BYTES }: { maxInputBytes?: number } = {},
): Promise<ValidatedImage> {
  if (!file || file.size === 0) throw new Error("That file is empty. Please choose an image.");
  if (file.size > maxInputBytes) {
    throw new Error(
      `"${file.name}" is ${(file.size / (1024 * 1024)).toFixed(1)} MB. Maximum upload size is ${Math.round(maxInputBytes / (1024 * 1024))} MB.`,
    );
  }

  const head = new Uint8Array(await file.slice(0, 512).arrayBuffer());
  const sniffed = sniffImageType(head);
  if (!sniffed) {
    throw new Error(`"${file.name}" is not a supported image. Use JPG, PNG, WebP, AVIF, GIF or SVG.`);
  }
  if (sniffed === "image/heic") {
    throw new Error("HEIC photos aren't supported. Please convert to JPG or PNG first.");
  }
  if (!(ACCEPTED_IMAGE_TYPES as readonly string[]).includes(sniffed)) {
    throw new Error(`"${file.name}" is not a supported image type.`);
  }

  // Normalise the declared MIME type to what the bytes actually say.
  const normalised = file.type === sniffed ? file : new File([file], file.name, { type: sniffed, lastModified: file.lastModified });
  return { file: normalised, type: sniffed };
}

function loadImage(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
    img.onerror = (e) => { URL.revokeObjectURL(url); reject(e); };
    img.src = url;
  });
}

function canvasToBlob(canvas: HTMLCanvasElement, type: string, quality: number): Promise<Blob | null> {
  return new Promise((resolve) => canvas.toBlob((b) => resolve(b), type, quality));
}

export const MAX_IMAGE_DIMENSION = 1920; // longest edge, px

export async function compressImage(
  input: File,
  maxBytes = MAX_IMAGE_BYTES,
  maxDimension = MAX_IMAGE_DIMENSION,
  options: { outputTypes?: string[]; maxInputBytes?: number } = {},
): Promise<File> {
  const { file } = await validateImageFile(input, options.maxInputBytes ? { maxInputBytes: options.maxInputBytes } : {});

  if (UNCOMPRESSIBLE.includes(file.type)) {
    if (file.size <= maxBytes) return file;
    throw new Error(
      `${file.type === "image/svg+xml" ? "SVG" : "GIF"} files cannot be compressed. Please upload a file under ${Math.round(maxBytes / 1024)} KB.`
    );
  }

  let img: HTMLImageElement;
  try {
    img = await loadImage(file);
  } catch {
    throw new Error("Could not read image for compression.");
  }

  // Always cap the longest edge to maxDimension so photos stay consistent.
  const longest = Math.max(img.naturalWidth, img.naturalHeight);
  const baseScale = longest > maxDimension ? maxDimension / longest : 1;

  const targetTypes = options.outputTypes ?? ["image/avif", "image/webp", "image/jpeg"];

  // If already small enough, no downscale needed and the format is allowed, keep the original.
  if (file.size <= maxBytes && baseScale === 1 && targetTypes.includes(file.type)) return file;

  // Prefer AVIF (best compression), then WebP, then JPEG. Browsers that don't
  // support AVIF via canvas.toBlob() will silently return null and we fall
  // through to WebP/JPEG.
  const scales = [1, 0.85, 0.7, 0.55, 0.45, 0.35, 0.28, 0.22].map((s) => s * baseScale);
  const qualities = [0.85, 0.75, 0.65, 0.55, 0.45, 0.35];

  let best: { blob: Blob; type: string } | null = null;

  for (const scale of scales) {
    const w = Math.max(64, Math.round(img.naturalWidth * scale));
    const h = Math.max(64, Math.round(img.naturalHeight * scale));
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("Canvas not supported in this browser.");
    ctx.drawImage(img, 0, 0, w, h);

    for (const type of targetTypes) {
      for (const q of qualities) {
        const blob = await canvasToBlob(canvas, type, q);
        if (!blob) continue;
        if (!best || blob.size < best.blob.size) best = { blob, type };
        if (blob.size <= maxBytes) {
          const ext = type === "image/avif" ? "avif" : type === "image/webp" ? "webp" : "jpg";
          const baseName = file.name.replace(/\.[^.]+$/, "") || "image";
          return new File([blob], `${baseName}.${ext}`, { type, lastModified: Date.now() });
        }
      }
    }
  }

  const smallestKb = best ? Math.round(best.blob.size / 1024) : Math.round(file.size / 1024);
  throw new Error(
    `Could not compress image below ${Math.round(maxBytes / 1024)} KB (smallest achievable: ${smallestKb} KB). Please upload a smaller image.`
  );
}

/**
 * Output formats accepted by the older upload endpoints (avatars, support
 * attachments) which only allow jpeg/png/webp/gif.
 */
export const LEGACY_OUTPUT_TYPES = ["image/webp", "image/jpeg"];
